import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    deepStringFind,
    Enums,
    errorRedirect,
    FormStore,
    inject,
    Input as CommonInput,
    NothingHere,
    SubTopBar,
    TasksStore,
    Title,
    toArray,
    toKeyArray,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArchiveFill } from '@react-icons/all-files/bs/BsArchiveFill';
import { BsBoxArrowUpRight } from '@react-icons/all-files/bs/BsBoxArrowUpRight';
import { RouteComponentProps } from 'react-router';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { AppStore } from '../Core/AppStore';
import { FileSystemsStore, IImportModel } from './FileSystemsStore';
import { FileSystemTypeList } from './FileSystemTypeList';

export interface IFileSystemsViewProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class FileSystemsView extends React.Component<IFileSystemsViewProps> {
    @action
    public async componentDidMount() {
        this.loading = true;

        try {
            await this.fileSystemsStore.loadAllAsync();
            await this.fileSystemsStore.loadMetaAsync();
        } catch (e: any) {
            errorRedirect(e);
        }

        this.loading = false;
    }

    public render() {
        return (
            <div className="container-fluid">
                <Title>File Systems</Title>

                <SubTopBar>
                    <Input
                        type="text"
                        placeholder="Search FileSystems..."
                        value={this.searchValue}
                        onChange={this.onSearchInputChange}
                        className="mw-450"
                    />

                    <div className="mx-2 border" />

                    <Form encType="multipart/form-data" onSubmit={this.uploadAsync}>
                        <FormGroup className="d-inline-block mb-0">
                            <input
                                type="submit"
                                value={this.uploading ? 'Uploading...' : 'Upload'}
                                className="btn btn-primary me-2"
                                disabled={this.uploading || !this.importForm.fields.files.value?.length}
                            />
                        </FormGroup>

                        <FormGroup className="d-inline-block mb-0" color={this.importForm.fields.files.state}>
                            <CommonInput
                                type="file"
                                id="file-system-upload"
                                field={this.importForm.fields.files}
                                required
                                multiple
                            />
                        </FormGroup>
                    </Form>

                    <div className="btn-group btn-group-sm d-inline-block ms-auto">
                        <div className="btn btn-link no-hover">
                            <BsBoxArrowUpRight />
                        </div>

                        <a
                            className="btn btn-link"
                            target="_blank"
                            href="https://support.mri-inc.net/hc/en-us/sections/115000852711-Firmware"
                        >
                            Public Changelogs
                        </a>

                        <a
                            className="btn btn-link"
                            target="_blank"
                            href="http://mri-perforce:8080/display/SWDEV/Software+Development+Home"
                        >
                            Legacy FileSystems
                        </a>
                    </div>
                </SubTopBar>

                <div className="row">
                    <div className="col-12 px-0">
                        <table className="table table-striped fixed-header">
                            <thead className="thead">
                                <tr>
                                    <th scope="col">Revision</th>
                                    <th scope="col">Updated</th>
                                    <th scope="col">Image</th>
                                    <th scope="col">Size</th>
                                    <th scope="col">Dependencies</th>
                                    <th scope="col">Changelog</th>
                                </tr>
                            </thead>

                            <tbody>
                                {toKeyArray(this.fileSystemsByFsType).map(k => (
                                    <FileSystemTypeList
                                        fileSystems={this.fileSystemsByFsType.get(k)!}
                                        fileSystemType={k}
                                        key={k}
                                        forceOpen={!!this.searchValue}
                                    />
                                ))}
                            </tbody>
                        </table>

                        <NothingHere
                            empty={this.fileSystems.length <= 0}
                            icon={<BsArchiveFill />}
                            loading={this.loading}
                            loadingMessage="Loading..."
                        />
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private async uploadAsync(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        this.uploading = true;
        this.importForm.fields.files.errors = [];
        const task = this.tasksStore.startSessionTask(`Upload file(s)`);

        try {
            await this.fileSystemsStore.uploadFile(this.importForm.values);
            task.complete();
            this.importForm.clear();
        } catch (e: any) {
            if (e.modelState) {
                this.importForm.addErrors(e.modelState);
            } else {
                this.importForm.fields.files.addErrors(e.message);
            }

            task.error(e);
        } finally {
            this.uploading = false;
        }
    }

    @bind
    @action
    private async onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @computed
    private get fileSystems() {
        let fileSystems = toArray(this.fileSystemsStore.fileSystems);

        if (this.searchValue)
            fileSystems = fileSystems!.filter(f => {
                const productTypeNames = Enums.FileSystemProductTypes.get(f.softwareVersion.fsType);

                const s = {
                    fileSystem: f,
                    fsType: Enums.toString(Enums.FileSystemType, f.softwareVersion.fsType),
                    prodType: productTypeNames?.get(f.softwareVersion.productType) ?? '',
                };

                return deepStringFind(s, this.searchValue);
            });

        return fileSystems;
    }

    @computed
    private get fileSystemsByFsType() {
        const aggregate: Map<Mri.DisplayApi.FileSystemTypes, Mri.FileSystems.FileSystem[]> = new Map<
        Mri.DisplayApi.FileSystemTypes,
        Mri.FileSystems.FileSystem[]
        >();

        this.sortFileSystems();

        this.fileSystems.reduce((agg, value) => {
            const key = value.softwareVersion.fsType;
            const arr = agg.get(key) ?? [];
            arr.push(value);
            return agg.set(key, arr);
        }, aggregate);

        return aggregate;
    }

    @bind
    private sortFileSystems() {
        this.fileSystems.sort((l, r) => {
            if (
                (l.softwareVersion.fsType === Mri.DisplayApi.FileSystemTypes.FS_TYPE_MRI_TI) !=
                (r.softwareVersion.fsType === Mri.DisplayApi.FileSystemTypes.FS_TYPE_MRI_TI)
            ) {
                return l.softwareVersion.fsType === Mri.DisplayApi.FileSystemTypes.FS_TYPE_MRI_TI ? -1 : 1;
            } else if (
                (l.softwareVersion.fsType === Mri.DisplayApi.FileSystemTypes.FS_TYPE_ROOT_TI) !=
                (r.softwareVersion.fsType === Mri.DisplayApi.FileSystemTypes.FS_TYPE_ROOT_TI)
            ) {
                return l.softwareVersion.fsType === Mri.DisplayApi.FileSystemTypes.FS_TYPE_ROOT_TI ? -1 : 1;
            } else {
                return (
                    r.softwareVersion.fsType - l.softwareVersion.fsType ||
                    r.softwareVersion.productType - l.softwareVersion.productType ||
                    r.softwareVersion.majorRevision - l.softwareVersion.majorRevision ||
                    r.softwareVersion.minorRevision - l.softwareVersion.minorRevision ||
                    r.softwareVersion.buildRevision - l.softwareVersion.buildRevision ||
                    l.softwareVersion.buildType - r.softwareVersion.buildType
                );
            }
        });
    }

    @observable private searchValue: string = '';
    @observable private loading = false;
    @observable private uploading = false;
    @observable
    private readonly importForm = new FormStore<IImportModel>({
        files: null,
    });

    @inject
    private fileSystemsStore!: FileSystemsStore;
    @inject
    private appStore!: AppStore;
    @inject
    private tasksStore!: TasksStore;
}
